import styled, { css } from 'styled-components';
import { Flex, IconArrowDown, IconFilters, spaceMixin, Text, token } from '@zillow/constellation';
import { Row } from '@zg-rentals/zrs-comet';
import { AdvancedTableStyles } from './AdvancedTable';

interface StyledTableColumnProps {
  align?: 'left' | 'right' | 'center';
  width?: string;
}

interface StyledFilterColumnHeaderProps {
  isClickable?: boolean;
}

const headerIconStyles = css`
  margin-left: 4px;
  flex-shrink: 0;
  color: ${token('colors.gray500')};
`;

const tableColumnStyles = css`
  display: inline-block;
  padding: 0 8px;
  text-rendering: auto;
  vertical-align: top;
  width: 100%;
  word-spacing: normal;
  zoom: 1;
`;

export const StyledExpandedContentContainer = styled.div`
  background-color: ${token('colors.blue100')};
  border-left: 1px solid ${token('colors.blue400')};
`;

export const StyledIconFilters = styled(IconFilters)`
  ${headerIconStyles};
`;

export const StyledTableBodyRow = styled(Flex)<{
  height?: string;
  width?: number;
  leftPadding?: string;
  rightPadding?: string;
  tableStyle?: AdvancedTableStyles;
  isSelected?: boolean;
  isExpanded?: boolean;
  rowNum: number;
}>`
  position: relative;
  ${(props) =>
    props.height &&
    css`
      height: ${props.height}px;
    `}
  ${(props) =>
    props.width &&
    css`
      width: ${props.width}px;
    `}
  border-left: 1px solid transparent;
  padding: ${(props) => props.paddingY || 10}px 0;
  border-bottom: 1px solid ${token('colors.gray300')};
  .Checkbox {
    padding-top: 4px;
  }

  .Text {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  ${(props) =>
    props.leftPadding &&
    css`
      padding-left: ${props.leftPadding};
    `}
  ${(props) =>
    props.rightPadding &&
    css`
      padding-right: ${props.rightPadding};
    `}
    ${(props) =>
    props.tableStyle === AdvancedTableStyles.standard &&
    css`
      &:hover {
        background: ${token('colors.backgroundLight')};
      }
    `}
  ${(props) =>
    props.isSelected &&
    css`
      background-color: ${token('colors.backgroundBlue')};
      &:hover {
        background: darken(${token('colors.backgroundBlue')}, 3%);
      }
    `}
  ${(props) =>
    props.isExpanded &&
    css`
      border-left: 1px solid ${token('colors.blue400')};
    `}
    ${(props) =>
    !props.isSelected &&
    props.tableStyle === AdvancedTableStyles.stripes &&
    props.rowNum % 2 == 0 &&
    css`
      background-color: ${token('colors.gray100')};
    `}
`;

export const StyledTableColumn = styled.div<StyledTableColumnProps>`
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-transform: none;
  font-family: 'Open Sans', 'Adjusted Arial', Tahoma, Geneva, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  margin: 0;

  height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  ${tableColumnStyles};
  width: ${(props) => props.width || '100%'};
  ${(props) =>
    props.align === 'center' &&
    css`
      text-align: center;
      .Text {
        text-align: center;
      }
      .TableMFH-header-cell {
        justify-content: center;
      }
    `};
  ${(props) =>
    props.align === 'right' &&
    css`
      text-align: right;
      .Text {
        text-align: right;
      }
      .TableMFH-header-cell {
        justify-content: flex-end;
      }
    `};
`;

export const StyledTableHeaderCell = styled(Flex)<{ isSortable?: boolean; align?: string }>`
  color: rgb(42, 42, 51);
  -webkit-font-smoothing: antialiased;
  text-transform: none;
  font-family: 'Open Sans', 'Adjusted Arial', Tahoma, Geneva, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 14px;

  display: flex;
  align-items: center;
  height: 100%;

  ${(props) =>
    props.isSortable &&
    css`
      cursor: pointer;
    `}
  ${(props) =>
    props.align === 'center' &&
    css`
      justify-content: center;
    `};
  ${(props) =>
    props.align === 'right' &&
    css`
      justify-content: flex-end;
    `};
`;

interface StyledTableHeaderStickyAreaProps {
  sticky?: boolean;
}

export const StyledTableHeaderStickyArea = styled.div<StyledTableHeaderStickyAreaProps>`
  background-color: ${token('colors.white')};
  overflow: hidden;
  z-index: 1;
  ${(props) =>
    props.sticky &&
    css`
      position: -webkit-sticky;
      position: sticky;
      top: 0px;
    `}
`;

export const StyledFilterColumnHeader = styled.div<StyledFilterColumnHeaderProps>`
  ${tableColumnStyles};
  ${(props) =>
    props.isClickable &&
    css`
      cursor: pointer;
    `}
`;

export const StyledFilterText = styled(Text)`
  margin-right: 4px;
  line-height: unset;
`;

export const StyledTableHeaderRow = styled(Flex)<{ width?: number; tableStyle?: AdvancedTableStyles }>`
  ${(props) =>
    props.width !== undefined &&
    props.width > 0 &&
    css`
      width: ${props.width}px;
    `}
  padding: 10px 0;
  height: 40px;
  box-sizing: content-box;
  border-bottom: 1px solid ${token('colors.gray300')};
  border-top: 1px solid ${token('colors.gray300')};
  background: ${(props) =>
    props.tableStyle === AdvancedTableStyles.stripes ? token('colors.coolGray200') : token('colors.white')};
`;

export const StyledIconChevronDown = styled(IconArrowDown)`
  ${headerIconStyles};
  ${(props) =>
    props.sortDirection === undefined &&
    css`
      color: #b3b3b3;
    `};
  ${(props) =>
    props.sortDirection === 'ascending' &&
    css`
      transform: rotate(0deg);
    `};
  ${(props) =>
    props.sortDirection === 'descending' &&
    css`
      transform: rotate(180deg);
    `};
`;

export const StyledIconContainer = styled(Flex)`
  transition: rotation 0.5s ease;
  height: 100%;
  align-items: center;
`;

export const StyledSelectAllContainer = styled(Flex)`
  ${tableColumnStyles};
`;

export const StyledColumnText = styled(Text)`
  line-height: unset;
`;

interface StyledFilterControlsContainerProps {
  isAnyFilterOpen?: boolean;
  isFilterOpen?: boolean;
}

export const StyledFilterControlsContainer = styled.div<StyledFilterControlsContainerProps>`
  transition: background-color 0.2s;
  ${(props) =>
    props.isAnyFilterOpen &&
    css`
      border-top: 1px solid ${token('colors.gray300')};
    `};
  ${(props) =>
    props.isFilterOpen &&
    css`
      padding: ${spaceMixin('lg')};
    `};
`;

export const StyledFilterControlsRow = styled(Row)`
  /* TODO: Remove !important. The default padding for Row is difficult to override */
  padding: ${spaceMixin('sm')} !important;
`;

export const StyledFilterPillsContainer = styled(Row)`
  .Col {
    padding: 0 ${spaceMixin('md')};
  }
  ${(props) =>
    props.isFilterOpen &&
    css`
      padding: ${spaceMixin('md')};
      height: auto;
    `};
`;

export interface StyledOverlayWrapperProps {
  isSelected: boolean;
  tableStyle: AdvancedTableStyles;
  rowNum: number;
}

export const StyledOverlayWrapper = styled.div<StyledOverlayWrapperProps>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: white;
  display: flex;
  align-items: center;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.4);
  clip-path: inset(0px -15px 0px 0px);
  ${(props) =>
    !props.isSelected &&
    props.tableStyle === AdvancedTableStyles.stripes &&
    props.rowNum % 2 == 0 &&
    css`
      background-color: ${token('colors.gray100')};
    `}
`;

export interface StyledOverlayWrapperPropsProps {
  tableStyle: AdvancedTableStyles;
  width: string;
}

export const StyledHeaderOverlayWrapper = styled.div<StyledOverlayWrapperPropsProps>`
  position: absolute;
  left: 0;
  top: 0;
  height: 40px;
  box-sizing: content-box;
  z-index: 5;
  padding: 10px 0;
  background-color: white;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.4);
  clip-path: inset(0px -15px 0px 0px);
  border-bottom: 1px solid #d1d1d5;
  border-top: 1px solid #d1d1d5;
  background: ${(props) =>
    props.tableStyle === AdvancedTableStyles.stripes ? token('colors.coolGray200') : token('colors.white')};
  width: ${(props) => props.width};
`;
